import { keyframes, styled } from "goober";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { contributors } from "../lib/contributors";
import { zIndexes } from "../lib/zIndexes";
import { CreditsMesh } from "./CreditsMesh";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\<>*+";

function randomGlyph() {
  return GLYPHS[(Math.random() * GLYPHS.length) | 0] ?? "*";
}

function scrambleAll(text: string) {
  return text.replace(/[^ ]/g, randomGlyph);
}

function revealText(text: string, progress: number) {
  const revealed = text.length * progress;
  return text
    .split("")
    .map((char, i) => {
      if (char === " ") return " ";
      if (i < revealed) return char;
      return randomGlyph();
    })
    .join("");
}

function runScramble(
  text: string,
  totalFrames: number,
  onFrame: (value: string) => void
) {
  let frame = 0;
  const id = setInterval(() => {
    frame += 1;
    onFrame(revealText(text, frame / totalFrames));
    if (frame >= totalFrames) {
      clearInterval(id);
      onFrame(text);
    }
  }, 45);
  return () => clearInterval(id);
}

// Decodes a name the first time it rolls into the central reading band, then
// leaves it decoded. `decoded` is shared across the whole roll so an already
// revealed name never re-encodes, even if React re-renders or remounts it.
function ScrambleName({
  text,
  decoded,
}: {
  text: string;
  decoded: Set<string>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState(() =>
    decoded.has(text) ? text : scrambleAll(text)
  );

  useEffect(() => {
    if (decoded.has(text)) return;
    const el = ref.current;
    if (!el) return;
    let stop: (() => void) | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        decoded.add(text);
        stop = runScramble(text, 16, setDisplay);
      },
      { rootMargin: "-35% 0px -11% 0px" }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      stop?.();
    };
  }, [text, decoded]);

  return <Name ref={ref}>{display}</Name>;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const roll = keyframes`
  from { transform: translateY(100vh); }
  to { transform: translateY(-100%); }
`;

const Overlay = styled("div")`
  position: fixed;
  inset: 0;
  z-index: ${zIndexes.credits};
  background: var(--surface-primary);
  color: var(--text-primary);
  font-family: var(--font-mono);
  overflow: hidden;
  cursor: pointer;
  animation: ${fadeIn} 600ms ease-out;
`;

const MeshLayer = styled("div")`
  position: absolute;
  inset: 0;
  opacity: 0.7;
  pointer-events: none;
`;

// Sits behind the names: darkens the busy mesh so the text stays legible.
const Scrim = styled("div")`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse 60% 100% at center,
    var(--surface-primary) 0%,
    transparent 75%
  );
`;

// Sits above the names: fades them in at the top and out at the bottom so
// nothing overlaps the exit hint.
const FadeMask = styled("div")`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    var(--surface-primary) 0%,
    transparent 14%,
    transparent 86%,
    var(--surface-primary) 95%
  );
`;

const Roll = styled("div")`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  padding: 0 24px;
  animation: ${roll} linear forwards;
`;

const Logo = styled("div")`
  font-size: clamp(80px, 16vw, 200px);
  line-height: 0;
  margin-bottom: 28px;
`;

const Tagline = styled("div")`
  font-size: 13px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 120px;
`;

const SectionLabel = styled("div")`
  font-size: 12px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin: 88px 0 28px;
`;

const Name = styled("div")`
  font-size: clamp(20px, 3vw, 30px);
  font-weight: 350;
  letter-spacing: -0.01em;
  padding: 6px 0;
`;

const Footer = styled("div")`
  margin: 120px 0 0;
  font-size: 14px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-secondary);
  line-height: 2.4;
`;

const ExitHint = styled("div")`
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  z-index: 1;
  text-align: center;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--text-secondary);
`;

function TinesMark({ size = 24 }: { size?: number | string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.38493 11.6925C7.38493 9.99306 6.00694 8.61507 4.30753 8.61507C2.60812 8.61507 1.23014 9.99306 1.23014 11.6925C1.23014 13.3919 2.60812 14.7699 4.30753 14.7699C6.00694 14.7699 7.38493 13.3919 7.38493 11.6925ZM8.61507 11.6925C8.61507 13.3919 9.99306 14.7699 11.6925 14.7699C13.3387 14.7699 14.6832 13.4768 14.7657 11.8511L14.7699 11.6925L14.7657 11.5338C14.6832 9.90812 13.3387 8.61507 11.6925 8.61507C9.31329 8.61507 7.38493 6.68671 7.38493 4.30753C7.38493 2.60812 6.00694 1.23014 4.30753 1.23014C2.60812 1.23014 1.23014 2.60812 1.23014 4.30753C1.23014 6.00694 2.60812 7.38493 4.30753 7.38493C6.68671 7.38493 8.61507 9.31329 8.61507 11.6925ZM8.61507 4.30753C8.61507 5.95374 9.90812 7.29825 11.5338 7.38081L11.6925 7.38493L11.8511 7.38081C13.4768 7.29825 14.7699 5.95374 14.7699 4.30753C14.7699 2.60812 13.3919 1.23014 11.6925 1.23014C9.99306 1.23014 8.61507 2.60812 8.61507 4.30753ZM16 11.6925C16 14.0716 14.0716 16 11.6925 16H4.30753C1.92836 16 0 14.0716 0 11.6925V4.30753C0 1.92836 1.92836 0 4.30753 0H11.6925C14.0716 0 16 1.92836 16 4.30753V11.6925Z"
        fill="var(--text-primary)"
      />
    </svg>
  );
}

export function Credits({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onClose]);

  // Pace the scroll to the amount of content so the roll stays readable.
  const durationSeconds = useMemo(
    () => Math.round(11 + contributors.length * 0.7),
    []
  );

  // Remembers which names have decoded so they never re-encode mid-visit.
  const decoded = useRef(new Set<string>()).current;

  return createPortal(
    <Overlay onClick={onClose}>
      <MeshLayer>
        <CreditsMesh />
      </MeshLayer>
      <Scrim />
      <Roll
        style={{ animationDuration: `${durationSeconds}s` }}
        onAnimationEnd={onClose}
      >
        <Logo>
          <TinesMark size="1em" />
        </Logo>
        <Tagline>Roll the credits</Tagline>

        <SectionLabel>Built by</SectionLabel>
        {/* oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed */}
        {contributors.map((name) => (
          <ScrambleName key={name} text={name} decoded={decoded} />
        ))}

        <Footer>Made with care</Footer>
      </Roll>
      <FadeMask />
      <ExitHint>Press esc or click anywhere to exit</ExitHint>
    </Overlay>,
    document.body
  );
}
